import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ConversationAnalysisService } from "@/services/conversation/ConversationAnalysisService";
import { ConversationDetails } from '@/types/conversation/conversation';

interface UseConversationAnalysisOptions {
  pageSize?: number;
  selectedAgent?: string | null;
  analysisFilter?: "all" | "analyzed" | "unanalyzed";
  dateRange?: string;
  enabled?: boolean;
  agents?: Array<{ id: string; name: string; elevenlabs_id?: string; branding?: any }>;
}

interface ConversationAnalysisPage {
  conversations: ConversationDetails[];
  totalCount: number;
  page: number;
  hasMore: boolean;
}

interface ConversationAnalysisResult {
  conversations: ConversationDetails[];
  totalCount: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  error: Error | null;
}

export const useConversationAnalysis = ({
  pageSize = 20,
  selectedAgent,
  analysisFilter = "all",
  dateRange = "30d",
  enabled = true,
  agents,
}: UseConversationAnalysisOptions = {}): ConversationAnalysisResult => {
  // Create a stable query key that includes all filters (but not page)
  const queryKey = useMemo(
    () => [
      "conversation-analysis-infinite",
      pageSize,
      selectedAgent,
      analysisFilter,
      dateRange,
      agents?.length || 0, // Include agents length to invalidate when agents change
    ],
    [pageSize, selectedAgent, analysisFilter, dateRange, agents?.length]
  );

  const { data, isLoading, isInitialLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }): Promise<ConversationAnalysisPage> => {
      // Get filtered data from service (server-side filtering)
      const result = await ConversationAnalysisService.getConversationAnalysisForUser({
        page: pageParam,
        pageSize,
        agentId: selectedAgent || undefined,
        analysisFilter,
        dateRange,
        agents,
      });

      const hasMore = result.page * result.pageSize < result.totalCount;

      return {
        conversations: result.data,
        totalCount: result.totalCount,
        page: pageParam,
        hasMore,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.page + 1 : undefined;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled,
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const allConversations = useMemo(() => {
    return data?.pages.flatMap((page) => page.conversations) || [];
  }, [data]);

  // Get total count from first page (now properly filtered on server-side)
  const totalCount = data?.pages[0]?.totalCount || 0;

  return {
    conversations: allConversations,
    totalCount,
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading || isLoading,
    error: error as Error | null,
  };
};

// Export a separate hook for stats that can be cached independently
export const useConversationAnalysisStats = (conversations: ConversationDetails[]) => {
  return useMemo(() => {
    return ConversationAnalysisService.getAnalysisStats(conversations);
  }, [conversations]);
};

// New hook for conversation stats using separate query
export const useConversationStats = (options: {
  selectedAgent?: string | null;
  agents?: Array<{ id: string; name: string; elevenlabs_id?: string; branding?: any }>;
  enabled?: boolean;
}) => {
  const { selectedAgent, agents, enabled = true } = options;

  const queryKey = useMemo(() => ["conversation-stats", selectedAgent, agents?.length || 0], [selectedAgent, agents?.length]);

  return useQuery({
    queryKey,
    queryFn: () =>
      ConversationAnalysisService.getConversationStatsForUser({
        selectedAgent,
        agents: agents || [],
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: enabled && agents && agents.length > 0,
  });
};
